import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Circle, FileUp, HardHat,
  Loader2, Sparkles, UploadCloud,
} from "lucide-react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { INTAKE_SECTIONS } from "../lib/projectIntake";
import { useAnalysisRunner, AnalysisProgressModal, AnalysisSubmittedCard } from "../lib/analysisRunner";
import { BuyCreditsModal } from "../components/founder/ReadinessReportCard";
import { maybeStartProjectIntakeTourOnFirstVisit } from "../lib/tour";
import { VentureSwitcher } from "../components/founder/VentureSwitcher";
import { ReportValueProp } from "../components/founder/ReportValueProp";
import { PROJECT_RAISE_BRACKETS } from "../lib/projectQuiz";
import { setWaContext } from "../lib/waContext";

const NAV = [
  { to: "/project/intake", label: "Project Intake" },
  { to: "/project/report", label: "Readiness Report" },
  { to: "/founder/team", label: "Team" },
  { to: "/founder/tutorial", label: "Tutorial" },
];

const REQ_BADGE = {
  required: "bg-rose-50 text-rose-700 border-rose-200",
  expected: "bg-amber-50 text-amber-700 border-amber-200",
  bonus: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function Field({ f, value, onChange }) {
  const base = "mt-1.5 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-[#00828F] focus:outline-none";
  return (
    <div>
      <label className="flex items-baseline gap-2 text-sm font-semibold text-neutral-800">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#00828F]">{f.q}</span>
        {f.label}
        {f.required && <span className="text-rose-500">*</span>}
      </label>
      {f.type === "textarea" ? (
        <textarea data-testid={`intake-${f.key}`} rows={3} className={base}
          placeholder={f.placeholder || ""} value={value ?? ""}
          onChange={(e) => onChange(f.key, e.target.value)} />
      ) : f.type === "select" ? (
        <select data-testid={`intake-${f.key}`} className={base} value={value ?? ""}
          onChange={(e) => onChange(f.key, e.target.value)}>
          <option value="">Select…</option>
          {f.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input data-testid={`intake-${f.key}`} className={base}
          type={f.type === "number" ? "number" : "text"} step="any"
          placeholder={f.placeholder || ""} value={value ?? ""}
          onChange={(e) => onChange(f.key, e.target.value)} />
      )}
    </div>
  );
}

function DocRow({ d, onUploaded, hideBadge = false }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", d.doc_type);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${d.label} uploaded`);
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3.5 py-3" data-testid={`doc-row-${d.doc_type}`}>
      {d.uploaded
        ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
        : <Circle className="h-5 w-5 flex-shrink-0 text-neutral-300" />}
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-semibold text-neutral-800">{d.label}</p>
        {!hideBadge && (
          <span className={`mt-0.5 inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${REQ_BADGE[d.requirement]}`}>
            {d.requirement} at your stage
          </span>
        )}
      </div>
      <label className={"inline-flex flex-shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors " +
        (d.uploaded ? "border border-[#004B62] bg-white text-[#004B62] hover:bg-neutral-50"
                    : "bg-[#004B62] text-white hover:bg-[#003a4d]")}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}
        {busy ? "Uploading…" : d.uploaded ? "Add another" : "Upload"}
        <input type="file" className="hidden" data-testid={`doc-upload-${d.doc_type}`}
          accept=".pdf,.pptx,.xlsx,.xlsm,.csv,.docx,.txt,.md,.jpg,.jpeg,.png,.heic,.heif"
          onChange={(e) => upload(e.target.files?.[0])} disabled={busy} />
      </label>
    </div>
  );
}

export default function ProjectIntake() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const { user } = useAuth();
  const [section, setSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  // Step-1 paywall (Sept 2026): intake + evidence + scoring stay hidden until paid.
  const [unlock, setUnlock] = useState(null); // { unlocked, tier, justPaid }
  const [payOpen, setPayOpen] = useState(false);
  const [bracketBusy, setBracketBusy] = useState(false);

  const loadUnlock = useCallback(async (paidNow = false) => {
    try {
      const { data: bal } = await api.get("/founder/credits/balance");
      setUnlock((u) => ({ unlocked: !!bal?.report_unlocked, tier: bal?.tier || null,
        justPaid: paidNow || !!u?.justPaid }));
    } catch (_e) { setUnlock({ unlocked: false, tier: null, justPaid: false }); }
  }, []);
  useEffect(() => { (async () => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("reference") || params.get("trxref");
    let paidNow = false;
    if (sid) {
      try {
        const { data: pollData } = await api.post(`/founder/credits/poll/${sid}`);
        paidNow = pollData?.payment_status === "paid";
      } catch (_e) { /* fall through */ }
      if (!paidNow) {
        try { const { data: rec } = await api.post("/founder/credits/reconcile"); paidNow = !!rec?.unlocked; } catch (_e) { /* ignore */ }
      }
      window.history.replaceState({}, "", window.location.pathname);
      if (paidNow) { try { window.dispatchEvent(new Event("clinq:credits-changed")); } catch (_e) { /* ignore */ } }
    }
    await loadUnlock(paidNow);
  })(); }, [loadUnlock]);

  const saveBracket = async (bracket) => {
    if (!bracket) return;
    setBracketBusy(true);
    try {
      await api.put("/project/raise-bracket", { bracket });
      await loadUnlock();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setBracketBusy(false); }
  };

  const totalSteps = INTAKE_SECTIONS.length + 1; // + documents step
  const isDocsStep = section === INTAKE_SECTIONS.length;

  useEffect(() => {
    if (!unlock) return;
    const label = !unlock.unlocked ? "Get my report" : isDocsStep ? "Upload evidence" : "Your project";
    setWaContext({ step: !unlock.unlocked ? 1 : isDocsStep ? 3 : 2, total: 5, label, company: data?.company_name });
    return () => setWaContext(null);
  }, [unlock, isDocsStep, data?.company_name]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/project/intake");
      setData(d);
      setAnswers((prev) => ({ ...d.intake, ...prev }));
    } catch (e) {
      if (e?.response?.status === 409) nav("/onboarding", { replace: true });
    }
  }, [nav]);
  useEffect(() => { load(); }, [load]);
  const dataLoaded = !!data;
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    api.get("/ai/analyze/inflight").then((r) => {
      const job = r.data?.job;
      if (job?.job_id) runner.resume(job.job_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (dataLoaded && section > 0) maybeStartProjectIntakeTourOnFirstVisit();
  }, [dataLoaded, section]);

  const runner = useAnalysisRunner({
    startEndpoint: "/project/analyze",
    onDone: () => {
      toast.success("Your Project Readiness Score is ready");
      nav("/project/report");
    },
    onError: (msg) => {
      if ((msg || "").toLowerCase().includes("credit")) { setBuyOpen(true); return; }
      toast.error(msg || "Analysis failed — please try again");
    },
  });

  const setAns = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const saveSection = async (fields) => {
    const payload = {};
    fields.forEach((f) => {
      const v = answers[f.key];
      if (v !== undefined && v !== "") payload[f.key] = v;
    });
    if (!Object.keys(payload).length) return null;
    setSaving(true);
    try {
      const { data: r } = await api.put("/project/intake", { answers: payload });
      setData((d) => (d ? { ...d, completeness: r.completeness } : d));
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save — try again");
      throw e;
    } finally { setSaving(false); }
  };

  const next = async () => {
    if (!isDocsStep) {
      try { await saveSection(INTAKE_SECTIONS[section].fields); } catch { return; }
    }
    if (section < totalSteps - 1) { setSection(section + 1); window.scrollTo(0, 0); }
  };

  const runAnalysis = async () => {
    if (!answers.project_category || !answers.stage) {
      toast.error("Complete the Project Basics section first");
      setSection(0);
      return;
    }
    await load();
    runner.run();
  };

  const comp = data?.completeness;
  const sec = !isDocsStep ? INTAKE_SECTIONS[section] : null;
  const visibleFields = useMemo(
    () => (sec ? sec.fields.filter((f) => !f.showIf || f.showIf(answers)) : []),
    [sec, answers]);

  return (
    <AppShell nav={NAV}>
      <AnalysisProgressModal busy={runner.busy} phase={runner.phase}
        testIdPrefix="prs-analysis" title="Scoring your project"
        note="Applying the Clinq Project Readiness methodology — land, permits, offtake, capital stack and risk" />
      {buyOpen && data && unlock?.tier && (
        <BuyCreditsModal tier={unlock.tier} onClose={() => setBuyOpen(false)} />
      )}
      {payOpen && unlock?.tier && (
        <BuyCreditsModal tier={unlock.tier} returnTo="onboarding" onClose={() => setPayOpen(false)} />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
          <HardHat className="h-5 w-5 text-amber-700" />
        </span>
        <div className="min-w-0">
          <h1 className="min-w-0 break-words text-2xl font-bold text-neutral-900 sm:text-3xl" data-testid="project-intake-title">
            {data?.company_name || "Your project"}
          </h1>
          <p className="text-sm text-neutral-600">Project Funding Readiness · PRS v1.0</p>
        </div>
        <VentureSwitcher />
        {data?.analysis_exists && (
          <button data-testid="intake-view-report-btn" onClick={() => nav("/project/report")}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-[#00828F] bg-white px-3 py-1.5 text-xs font-semibold text-[#00828F] hover:bg-[#E5EEF1]">
            View my report <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {unlock === null && (
        <div className="flex justify-center py-24" data-testid="intake-unlock-loading"><Loader2 className="h-7 w-7 animate-spin text-[#00828F]" /></div>
      )}

      {unlock && !unlock.unlocked && (
        <div className="mt-2 max-w-3xl" data-testid="project-paywall-step">
          <ReportValueProp key={unlock.tier ? "priced" : "unpriced"} variant="project" onUnlock={() => setPayOpen(true)} />
          {!unlock.tier && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="project-raise-bracket-picker">
              <p className="text-sm font-bold text-amber-900">How much are you raising for this project?</p>
              <p className="mt-1 text-xs text-amber-800">Your report price is set by the amount you are raising.</p>
              <select data-testid="project-raise-bracket-select" disabled={bracketBusy} defaultValue=""
                onChange={(e) => saveBracket(e.target.value)}
                className="mt-3 w-full max-w-sm rounded-lg border border-amber-300 bg-white px-3 py-2.5 text-sm text-neutral-900 focus:border-[#00828F] focus:outline-none">
                <option value="">Select the amount…</option>
                {PROJECT_RAISE_BRACKETS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {unlock?.unlocked && unlock.justPaid && section === 0 && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4" data-testid="intake-paid-banner">
          <p className="text-sm font-bold text-emerald-800">Payment confirmed — your Project Readiness Report is unlocked.</p>
          <p className="mt-1 text-sm text-emerald-700">Complete the intake below, upload your evidence, then get your Project Readiness Score. Your 3 re-run credits are ready.</p>
        </div>
      )}

      {unlock?.unlocked && comp && (
        <div className="mt-4 rounded-xl border border-neutral-200 bg-white px-4 py-3" data-testid="intake-completeness">
          <div className="flex items-center justify-between text-xs font-semibold text-neutral-600">
            <span>Core intake completeness</span><span>{comp.core_pct}%</span>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-neutral-100">
            <div className="h-2 rounded-full bg-[#00828F] transition-all" style={{ width: `${comp.core_pct}%` }} />
          </div>
          {comp.is_provisional && (
            <p className="mt-1.5 text-xs text-neutral-500">
              You can score with gaps — but missing answers and documents hold points back.
            </p>
          )}
        </div>
      )}

      {/* Section stepper */}
      {unlock?.unlocked && (<>
      <div className="-mx-4 mt-6 flex gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:px-0" data-testid="intake-stepper">
        {[...INTAKE_SECTIONS.map((s) => s.title), "Documents & Score"].map((t, i) => (
          <button key={t} onClick={() => setSection(i)} data-testid={`intake-step-${i}`}
            className={"flex-shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors " +
              (i === section ? "bg-[#004B62] text-white"
               : "border border-neutral-200 bg-white text-neutral-600 hover:border-[#00828F]/50")}>
            {i + 1}. {t}
          </button>
        ))}
      </div>

      <div className="mt-6 max-w-3xl">
        {!isDocsStep ? (
          <section className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6" data-testid={`intake-section-${sec.id}`}>
            <div className="flex items-center gap-2">
              {sec.pillar && (
                <span className="rounded-md bg-[#E5EEF1] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#00828F]">
                  Pillar {sec.pillar}
                </span>
              )}
              <h2 className="text-lg font-bold text-neutral-900">{sec.title}</h2>
            </div>
            <p className="mt-1 text-sm text-neutral-600">{sec.blurb}</p>
            <div className="mt-5 space-y-4">
              {visibleFields.map((f) => (
                <Field key={f.key} f={f} value={answers[f.key]} onChange={setAns} />
              ))}
            </div>
          </section>
        ) : (
          <section className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6" data-testid="intake-section-docs">
            <h2 className="text-lg font-bold text-neutral-900">Evidence documents</h2>
            <p className="mt-1 text-sm text-neutral-600">
              The PRS scores evidence, not claims — a claim without its document earns
              partial credit at best. Your stage sets what's required.
            </p>
            {(() => {
              const list = data?.doc_checklist || [];
              const allRequired = list.length > 1 && list.every((d) => d.requirement === "required");
              return (
                <>
                  {allRequired && (
                    <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700" data-testid="docs-all-required-note">
                      All documents below are required at your stage.
                    </p>
                  )}
                  <div className="mt-4 space-y-2.5">
                    {list.map((d) => (
                      <DocRow key={d.doc_type} d={d} onUploaded={load} hideBadge={allRequired} />
                    ))}
                  </div>
                </>
              );
            })()}
            <div className="mt-6 rounded-xl border border-[#00828F]/25 bg-[#E5EEF1] p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#00828F]" />
                <div>
                  <p className="text-sm font-bold text-neutral-900">Ready to be scored?</p>
                  <p className="mt-0.5 text-sm text-neutral-600">
                    We'll apply the six PRS pillars, run the ten hard checks, and weight
                    the result for your target funder type.
                    {data?.analysis_exists && " Re-runs use 1 credit."}
                  </p>
                  {runner.submitted ? <div className="mt-3"><AnalysisSubmittedCard email={user?.email} venture="project" testIdPrefix="prs-analysis" /></div> : (
                  <button data-testid="run-prs-analysis-btn" onClick={runAnalysis} disabled={runner.busy}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-[#003a4d] disabled:opacity-60">
                    {runner.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                    {data?.analysis_exists ? "Re-run Project Readiness Analysis" : "Get my Project Readiness Score"}
                  </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        <div className="mt-5 flex items-center justify-between">
          {section > 0 ? (
            <button data-testid="intake-back-btn" onClick={() => { setSection(section - 1); window.scrollTo(0, 0); }}
              className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-600 hover:text-neutral-900">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : <span />}
          {!isDocsStep && (
            <button data-testid="intake-next-btn" onClick={next} disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-6 py-2.5 text-sm font-semibold text-white shadow hover:bg-[#003a4d] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save & continue <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      </>)}
    </AppShell>
  );
}
